"use client";

import { useCallback, useRef } from "react";
import type { ChordDefinition } from "../chordAudio";
import { type SVGuitarDimensions, SVGuitarRenderer } from "../SVGuitarRenderer";
import { type FretboardState, stateToFrets } from "./ChordIdentifier";

const TOTAL_FRETS = 12;
const NUM_STRINGS = 6;

interface InteractiveFretboardProps {
  state: FretboardState;
  onChange: (state: FretboardState) => void;
  /** Optional fingering to display on the dots */
  fingering?: (number | "-")[];
  /** Called when user strums across strings */
  onStrum?: (stringIndices: number[]) => void;
}

function stateToChordDefinition(state: FretboardState): ChordDefinition {
  return { name: "", baseFret: 1, frets: stateToFrets(state) };
}

export function InteractiveFretboard({
  state,
  onChange,
  fingering,
  onStrum,
}: InteractiveFretboardProps) {
  const strumAreaRef = useRef<HTMLDivElement>(null);
  const dimensionsRef = useRef<SVGuitarDimensions | null>(null);

  // Track which strings have been strummed during current gesture
  const strummedStringsRef = useRef<Set<number>>(new Set());
  const isStrummingRef = useRef(false);

  // Get string index from x position
  const getStringIndexFromX = useCallback((clientX: number): number | null => {
    const dims = dimensionsRef.current;
    if (!dims || !strumAreaRef.current) return null;
    const rect = strumAreaRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const stringIndex = Math.round((x - dims.leftPadding) / dims.stringSpacing);
    if (stringIndex >= 0 && stringIndex < NUM_STRINGS) {
      return stringIndex;
    }
    return null;
  }, []);

  // Handle strum gesture
  const handleStrumStart = useCallback(
    (clientX: number) => {
      isStrummingRef.current = true;
      strummedStringsRef.current = new Set();
      const stringIndex = getStringIndexFromX(clientX);
      if (stringIndex !== null) {
        strummedStringsRef.current.add(stringIndex);
      }
    },
    [getStringIndexFromX],
  );

  const handleStrumMove = useCallback(
    (clientX: number) => {
      if (!isStrummingRef.current || !onStrum) return;
      const stringIndex = getStringIndexFromX(clientX);
      if (
        stringIndex !== null &&
        !strummedStringsRef.current.has(stringIndex)
      ) {
        strummedStringsRef.current.add(stringIndex);
        // Play single string as it's crossed
        onStrum([stringIndex]);
      }
    },
    [getStringIndexFromX, onStrum],
  );

  const handleStrumEnd = useCallback(() => {
    isStrummingRef.current = false;
    strummedStringsRef.current = new Set();
  }, []);

  const handleNutTap = useCallback(
    (stringIndex: number) => {
      const currentState = state[stringIndex];
      if (!currentState) return;

      const newState = [...state] as FretboardState;

      if (currentState.type === "fretted") {
        newState[stringIndex] = { type: "open" };
      } else if (currentState.type === "open") {
        newState[stringIndex] = { type: "muted" };
      } else {
        newState[stringIndex] = { type: "open" };
      }

      onChange(newState);
    },
    [state, onChange],
  );

  const handleFretTap = useCallback(
    (stringIndex: number, fret: number) => {
      const currentState = state[stringIndex];
      if (!currentState) return;

      const newState = [...state] as FretboardState;

      if (currentState.type === "fretted" && currentState.fret === fret) {
        newState[stringIndex] = { type: "open" };
      } else {
        newState[stringIndex] = { type: "fretted", fret };
      }

      onChange(newState);
    },
    [state, onChange],
  );

  const chord = stateToChordDefinition(state);

  return (
    <div className="flex flex-col items-center">
      <div className="overflow-y-auto max-h-96 scrollbar-hidden">
        <SVGuitarRenderer
          chord={chord}
          fingering={fingering}
          frets={TOTAL_FRETS}
          width={240}
          showFretMarkers
        >
          {(dimensions) => {
            // Store dimensions in ref for strum handlers
            dimensionsRef.current = dimensions;

            return (
              <>
                {/* Strum area - captures horizontal swipes for strumming */}
                {onStrum && (
                  <div
                    ref={strumAreaRef}
                    className="absolute touch-none"
                    style={{
                      left: 0,
                      right: 0,
                      top: dimensions.nutY,
                      bottom: 0,
                      pointerEvents: "auto",
                    }}
                    onPointerDown={(e) => {
                      e.currentTarget.setPointerCapture(e.pointerId);
                      handleStrumStart(e.clientX);
                    }}
                    onPointerMove={(e) => handleStrumMove(e.clientX)}
                    onPointerUp={handleStrumEnd}
                    onPointerCancel={handleStrumEnd}
                  />
                )}

                {/* Clickable overlay */}
                <div
                  className="absolute inset-0"
                  style={{ pointerEvents: "none" }}
                >
                  {/* Nut click areas */}
                  {Array.from({ length: NUM_STRINGS }, (_, stringIndex) => {
                    const x =
                      dimensions.leftPadding +
                      stringIndex * dimensions.stringSpacing;

                    // String index is stable (0-5 for 6 guitar strings, never reordered)
                    return (
                      <button
                        // biome-ignore lint/suspicious/noArrayIndexKey: Fixed array of 6 guitar strings
                        key={`nut-${stringIndex}`}
                        type="button"
                        className="absolute cursor-pointer"
                        style={{
                          left: x - 22,
                          top: 0,
                          width: 44,
                          height: Math.max(44, dimensions.nutY + 10),
                          pointerEvents: "auto",
                        }}
                        onClick={() => handleNutTap(stringIndex)}
                        aria-label={`String ${stringIndex + 1} nut`}
                      />
                    );
                  })}

                  {/* Fret click areas */}
                  {Array.from({ length: TOTAL_FRETS }, (_, fretIndex) => {
                    const fret = fretIndex + 1;
                    const y =
                      dimensions.nutY +
                      (fretIndex + 0.5) * dimensions.fretSpacing;

                    return Array.from(
                      { length: NUM_STRINGS },
                      (_, stringIndex) => {
                        const x =
                          dimensions.leftPadding +
                          stringIndex * dimensions.stringSpacing;

                        return (
                          <button
                            key={`fret-${stringIndex}-${fret}`}
                            type="button"
                            className="absolute cursor-pointer hover:bg-text-primary/10 rounded-full"
                            style={{
                              left: x - 22,
                              top: y - 22,
                              width: 44,
                              height: 44,
                              pointerEvents: "auto",
                            }}
                            onClick={() => handleFretTap(stringIndex, fret)}
                            aria-label={`String ${stringIndex + 1}, fret ${fret}`}
                          />
                        );
                      },
                    );
                  })}
                </div>
              </>
            );
          }}
        </SVGuitarRenderer>
      </div>
    </div>
  );
}
